import { useField } from 'formik'
import { useState } from 'react'
import { MdOpenInNew } from 'react-icons/md'

import type { IBasicUserInfo } from './types'
import type { ReactElement, ChangeEvent, MouseEvent } from 'react'
import type { IAssetFile } from '~/types/file'
import type { IAssetOption } from '~/types/option'
import type { IPlaceholderRule } from '~/types/template'
import type { IUserPublic } from '~/types/user'

import AssetDetails from './AssetDetails'
import TooltipOverlay from './TooltipOverlay'
import UserInfos from './UserInfos'
import WarningTooltip from './WarningTooltip'

import ActionInput from '../ActionInput'
import InputControls from '../Controls'

import { AssetStatusBadge } from '~/components/Badges'
import Button from '~/components/Buttons/Button'
import FilePreviewModal from '~/components/FilePreviewModal'
import TextInput from '~/components/Inputs/Basic/TextInput'
import Modal from '~/components/Modal'
import { ModalBody, ModalHeader } from '~/components/Modal/Layout'
import { useAssetsSearchQuery } from '~/queries/assets'
import { useUserDetailsQueries } from '~/queries/users'
import { getFileType } from '~/utils/file'
import { debounce } from '~/utils/function'

import './styles.scss'

interface Props {
  disabled?: boolean
  focus?: boolean
  name: string
  rules?: IPlaceholderRule[]
}

const getMaxFileSize = (files: IAssetFile[]) => {
  let maxFileSize = 0
  files.forEach(({ size }) => {
    if (size >= maxFileSize) {
      maxFileSize = size
    }
  })

  return maxFileSize < 10000 ? '< 10Kb' : `${(maxFileSize / 1000000).toFixed(2)}Mb`
}

const assetDetails = (value: IAssetOption, users?: IBasicUserInfo[]) => {
  return (
    <table className="asset-details-table">
      <thead>
        <tr>
          <th>Status</th>
          <th>Type</th>
          <th>Max file size</th>
          <th>Last update by</th>
        </tr>
      </thead>
      <tbody>
        <tr className="detail-row">
          <td>
            <AssetStatusBadge size="medium" value={value.status} />
          </td>
          <td>{value.fileType}</td>
          <td>{value.files ? getMaxFileSize(value.files) : null}</td>
          <td>
            <UserInfos backgroundColor="default" option={value} users={users} />
          </td>
        </tr>
      </tbody>
    </table>
  )
}

const getAssetPreviewComponent = (asset: IAssetOption) => {
  if (!asset.files || asset?.files?.length === 0) return null

  const { download_url, mime_type } = asset.files[0]

  return getFileType(mime_type) === 'video' ? (
    <video autoPlay className="asset-image" loop muted src={download_url} />
  ) : (
    <img className="asset-image" src={download_url} />
  )
}

const AssetInput = ({ disabled, name, rules }: Props): ReactElement => {
  const [inputText, setInputText] = useState('')

  const [isSelectModalOpen, setIsSelectModalOpen] = useState(false)

  const [isFilePreviewModalOpen, setIsFilePreviewModalOpen] = useState(false)

  const { data: assets } = useAssetsSearchQuery(inputText)

  const [, { error, value }, { setValue }] = useField<IAssetOption | undefined>(name)

  const options: IAssetOption[] =
    assets?.data?.map(({ file_type, files, id, name, status, updated_at, updated_by }) => ({
      fileType: file_type,
      files,
      id,
      label: name,
      status,
      updatedAt: updated_at,
      updatedBy: updated_by,
      value: id,
    })) || []

  const formatUser = ({ name, picture, user_id }: IUserPublic) => ({
    name,
    picture,
    user_id,
  })

  const userQueries = useUserDetailsQueries(
    options.map(({ updatedBy }) => updatedBy),
    formatUser
  )

  const users = userQueries.some((e) => e.isLoading)
    ? []
    : userQueries.map(({ data }) => data as IBasicUserInfo).filter((data) => !!data)

  const handleInputChange = debounce((evt: ChangeEvent<HTMLInputElement>) => {
    setInputText(evt.target.value)
  }, 500)

  const handleSelectClick = () => {
    if (disabled) {
      setIsFilePreviewModalOpen(true)
    } else {
      setIsSelectModalOpen(true)
    }
  }

  const handleAssetClick = (option: IAssetOption) => {
    setValue(option)
    setIsSelectModalOpen(false)
    setInputText('')
  }

  const handleModalClose = () => {
    setIsSelectModalOpen(false)
    setInputText('')
  }

  const handleAssetDetailLinkClick = (assetId: string, evt: MouseEvent<HTMLDivElement>) => {
    evt.stopPropagation()
    window.open(`${location.origin}/dynamic-screens/assets-management/${assetId}`, '_blank')
  }

  const handleDelete = () => setValue(undefined)

  return (
    <div className="asset-input-component">
      <div className="asset-input-component__row">
        {value ? (
          <div className="asset-input-component__row__image" onClick={handleSelectClick}>
            {getAssetPreviewComponent(value)}
          </div>
        ) : null}
        <ActionInput
          className="asset-input-component__row__input"
          disabled={disabled}
          isValid={!error && !!value}
          label="asset"
          onAddClick={handleSelectClick}
          onInputClick={handleSelectClick}
        >
          {value ? value.label : null}
        </ActionInput>
        {value ? <WarningTooltip asset={value} rules={rules} /> : null}
        {value ? <InputControls copyValue={value.label} onDelete={!disabled ? handleDelete : undefined} /> : null}
      </div>
      {value?.files?.length ? <div className="asset-input-component__row">{assetDetails(value, users)}</div> : null}
      <Modal className="assets-preview-modal" isOpen={isSelectModalOpen} onCloseModal={handleModalClose}>
        <ModalHeader className="assets-preview-modal__header">
          <div className="assets-preview-modal__search-container">
            <TextInput
              autoFocus
              label="Select an asset"
              onChange={handleInputChange}
              placeholder="Search"
              tooltip={<TooltipOverlay />}
              wrapperClassName="assets-preview-modal__search-container__input"
            />
          </div>
        </ModalHeader>
        <ModalBody className="assets-preview-modal__body">
          <div className="modal-body-content">
            {options.length === 0 ? (
              <span className="no-assets-message">No assets found.</span>
            ) : (
              <div className="assets-preview-container">
                {options.map(
                  (option, idx) =>
                    option.files && (
                      <div className="asset-container" key={`image-${idx}`}>
                        <WarningTooltip asset={option} className="asset-warning-icon" rules={rules} />
                        {getAssetPreviewComponent(option)}
                        <div className="asset-infos-container">
                          <div className="asset-info-row">
                            <div className="asset-name">{option.label}</div>
                          </div>
                          <div className="asset-info-row">
                            <AssetDetails
                              backgroundColor="black"
                              maxFileSize={getMaxFileSize(option.files)}
                              option={option}
                              users={users}
                            />
                          </div>
                        </div>
                        <div className="asset-cover">
                          <div onClick={(e) => handleAssetDetailLinkClick(option.id, e)}>
                            <MdOpenInNew className="asset-detail-link-icon" />
                          </div>
                          <Button onClick={() => handleAssetClick(option as IAssetOption)}>Select</Button>
                        </div>
                      </div>
                    )
                )}
              </div>
            )}
          </div>
        </ModalBody>
      </Modal>
      {value?.files && disabled ? (
        <FilePreviewModal
          downloadUrl={value.files[0].download_url}
          fileType={value.files[0].mime_type}
          isOpen={isFilePreviewModalOpen}
          onClose={() => setIsFilePreviewModalOpen(false)}
        />
      ) : null}
    </div>
  )
}

export default AssetInput
